import { MAX_GRID_WIDTH } from "config/styleConsts";
import { useReviews } from "contexts/DataContext";
import useDeviceSize from "hooks/useDeviceSize";
import { useLanguage } from "contexts/LocalizationContext";
import "keen-slider/keen-slider.min.css";
import { useKeenSlider } from "keen-slider/react";
import type { KeenSliderOptions } from "keen-slider/react";
import chunk from "lodash/chunk";
import { useState, useEffect, useMemo } from "react";
import { Box, Flex, HStack } from "@chakra-ui/react";
import type { SbEditableComponentProps } from "types/storyblok";
import { Dots } from "./Dots";
import { ReviewCard } from "./ReviewCard";

export const ReviewSlider = ({ sbEditable }: SbEditableComponentProps<unknown>) => {
  const { isTablet, isMobileOrTablet } = useDeviceSize();
  const reviews = useReviews();
  const [currentSlide, setCurrentSlide] = useState(0);
  const language = useLanguage();

  const sliderOptions: KeenSliderOptions = useMemo(
    () => ({
      mode: "snap",
      slides: {
        origin: "center",
        perView: isMobileOrTablet ? 1 : "auto",
        spacing: isMobileOrTablet ? 0 : 32,
      },
      initial: 0,
      slideChanged(slider) {
        setCurrentSlide(slider.track.details.rel);
      },
    }),
    [isMobileOrTablet]
  );

  const [sliderRef, instanceRef] = useKeenSlider(sliderOptions);

  useEffect(() => {
    instanceRef.current.update(sliderOptions);
  }, [instanceRef, language, sliderOptions]);

  const goToSlide = (slideIndex: number) => instanceRef.current?.moveToIdx(slideIndex);

  const chunkedDesktopReviews = chunk(reviews, 3);
  const desktopReviews = chunkedDesktopReviews?.map((reviewChunk) => (
    <Flex
      className="keen-slider__slide"
      key={`${reviewChunk[0].id}-${reviewChunk[1]?.id}`}
      minW="fit-content"
      maxW="fit-content"
    >
      <HStack spacing={{ lg: 4, xl: 8 }} align="stretch" px={{ base: "unset", lg: 4, xl: "unset" }}>
        {reviewChunk.map((review) => (
          <ReviewCard key={review.id} review={review} />
        ))}
      </HStack>
    </Flex>
  ));

  const chunkedMobileReviews = isTablet ? chunk(reviews, 2) : chunk(reviews, 1);
  const mobileReviews = chunkedMobileReviews?.map((reviewChunk) => (
    <Flex className="keen-slider__slide" key={`${reviewChunk[0].id}`}>
      <HStack px={4} align="stretch" w="full" spacing={4}>
        {reviewChunk.map((review) => (
          <ReviewCard review={review} key={review.id} />
        ))}
      </HStack>
    </Flex>
  ));

  const content = isMobileOrTablet ? mobileReviews : desktopReviews;
  const numberOfSlides = isMobileOrTablet ? chunkedMobileReviews.length : chunkedDesktopReviews.length;

  return (
    <>
      <Box ref={sliderRef} className="keen-slider" {...sbEditable}>
        {content}
      </Box>
      <Flex justify={{ base: "center", xl: "flex-start" }} pt={10} maxW={MAX_GRID_WIDTH} mx="auto">
        <Dots currentSlide={currentSlide} numberOfSlides={numberOfSlides} goToSlide={goToSlide} />
      </Flex>
    </>
  );
};
